import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Dropdown, Icon } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { UpdateUserPreferredLanguage } from "src/graphql/users";
import {
  getAppLanguagesForUser,
  isAppLanguage,
  writeStoredAppLanguage,
} from "src/utils/appLanguage";

const stopEvent = (event) => {
  event.stopPropagation();
};

const HeaderLanguageSelector = ({
  placement = "header",
  userMenuOpen = false,
  onSubmenuOpenChange,
}) => {
  const { t, i18n } = useTranslation("header");
  const uid = useSelector((state) => state.auth?.uid);
  const userEmail = useSelector((state) => state.auth?.email);
  // El japonés solo se ofrece a las cuentas habilitadas (ver appLanguage.js)
  const languages = useMemo(() => getAppLanguagesForUser(userEmail), [userEmail]);
  const { executeMutation: updatePreferredLanguage } = UpdateUserPreferredLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const setLanguage = useCallback(
    async (code, event) => {
      stopEvent(event);
      if (!isAppLanguage(code) || code === i18n.language) {
        setMenuOpen(false);
        onSubmenuOpenChange?.(false);
        return;
      }
      writeStoredAppLanguage(code);
      await i18n.changeLanguage(code);
      setMenuOpen(false);
      onSubmenuOpenChange?.(false);
      if (uid) {
        const { error } = await updatePreferredLanguage({ language: code });
        if (error) {
          console.warn("updateUserPreferredLanguage:", error.message);
        }
      }
    },
    [i18n, uid, updatePreferredLanguage, onSubmenuOpenChange],
  );

  const currentLanguage =
    languages.find(({ code }) => code === i18n.language) || languages[0];

  const isMenu = placement === "menu";

  const updatePanelPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPanelStyle({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, []);

  const closeSubmenu = useCallback(() => {
    setMenuOpen(false);
    onSubmenuOpenChange?.(false);
  }, [onSubmenuOpenChange]);

  const toggleSubmenu = useCallback(
    (event) => {
      stopEvent(event);
      setMenuOpen((open) => {
        const nextOpen = !open;
        onSubmenuOpenChange?.(nextOpen);
        return nextOpen;
      });
    },
    [onSubmenuOpenChange],
  );

  useEffect(() => {
    if (!userMenuOpen) {
      setMenuOpen(false);
    }
  }, [userMenuOpen]);

  useEffect(() => {
    if (!isMenu || !menuOpen) return undefined;

    updatePanelPosition();

    const handleOutside = (event) => {
      if (
        menuRef.current?.contains(event.target) ||
        panelRef.current?.contains(event.target)
      ) {
        return;
      }
      closeSubmenu();
    };

    const handleReposition = () => updatePanelPosition();

    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isMenu, menuOpen, closeSubmenu, updatePanelPosition]);

  if (isMenu) {
    return (
      <div
        ref={menuRef}
        className="headerLanguageNav headerLanguageNav--menu"
        onMouseDown={stopEvent}
        onClick={stopEvent}
      >
        <button
          ref={triggerRef}
          type="button"
          className="headerLanguageButton"
          aria-label={t("userMenu.language")}
          aria-expanded={menuOpen}
          onClick={toggleSubmenu}
        >
          <span className="headerLanguageButton__flag" aria-hidden="true">
            {currentLanguage.flag}
          </span>
          <Icon name="caret down" className="headerLanguageButton__caret" />
        </button>

        {menuOpen &&
          panelStyle &&
          createPortal(
            <div
              ref={panelRef}
              className="headerLanguageMenuPanel headerLanguageMenuPanel--portal"
              style={panelStyle}
              role="menu"
              onMouseDown={stopEvent}
              onClick={stopEvent}
            >
              {languages.map(({ code, labelKey, flag }) => (
                <button
                  key={code}
                  type="button"
                  role="menuitem"
                  className={`headerLanguageMenuPanel__item${
                    code === i18n.language ? " is-active" : ""
                  }`}
                  onMouseDown={stopEvent}
                  onClick={(event) => setLanguage(code, event)}
                >
                  <span className="headerLanguageMenuPanel__flag" aria-hidden="true">
                    {flag}
                  </span>
                  <span className="headerLanguageMenuPanel__label">{t(labelKey)}</span>
                </button>
              ))}
            </div>,
            document.body,
          )}
      </div>
    );
  }

  return (
    <div className="headerLanguageNav">
      <Dropdown
        selectOnBlur={false}
        className="headerLanguageDropdown noCaret"
        trigger={
          <button
            type="button"
            className="headerLanguageButton"
            aria-label={t("userMenu.language")}
          >
            <span className="headerLanguageButton__flag" aria-hidden="true">
              {currentLanguage.flag}
            </span>
            <Icon name="caret down" className="headerLanguageButton__caret" />
          </button>
        }
      >
        <Dropdown.Menu className="headerLanguageMenu">
          {languages.map(({ code, labelKey, flag }) => (
            <Dropdown.Item
              key={code}
              className={`headerLanguageMenu__item${
                code === i18n.language ? " active" : ""
              }`}
              onClick={(event) => setLanguage(code, event)}
            >
              <span className="headerLanguageMenu__flag" aria-hidden="true">
                {flag}
              </span>
              <span className="headerLanguageMenu__label">{t(labelKey)}</span>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default HeaderLanguageSelector;
